import React from 'react';
import { ThemeProvider } from 'styled-components';
import { Skeleton } from 'antd';
import { Typography } from 'antd';
import { useTheme } from '../../../context/ThemeContext';
import {
  StyledCard,
  DailyBreakdownItem,
  DailyBreakdownHeader,
  DailyBreakdownDay,
  DailyBreakdownAmount,
  StyledProgress,
  EmptyStateText,
} from './Styles';
import { addCommasInPrices, EMPTY_STATE_MESSAGES } from './constants';
import { DailyBreakdownSectionProps } from './types';

const { Title } = Typography;

const DailyBreakdownSection: React.FC<DailyBreakdownSectionProps> = ({
  reportData,
  loading = false,
}) => {
  const { isDarkMode } = useTheme();
  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <StyledCard title={<Title level={4} style={{ margin: 0 }}>Daily Breakdown</Title>}>
          {Array.from({ length: 7 }).map((_, index) => (
            <DailyBreakdownItem key={index}>
              <Skeleton active paragraph={{ rows: 1 }} />
            </DailyBreakdownItem>
          ))}
        </StyledCard>
      </ThemeProvider>
    );
  }

  if (!reportData?.dailyBreakdown) {
    return (
      <ThemeProvider theme={theme}>
        <StyledCard title={<Title level={4} style={{ margin: 0 }}>Daily Breakdown</Title>}>
          <EmptyStateText>{EMPTY_STATE_MESSAGES.DAILY_BREAKDOWN}</EmptyStateText>
        </StyledCard>
      </ThemeProvider>
    );
  }

  const dailyData = reportData.dailyBreakdown;
  const days = Object.keys(dailyData);
  const maxValue = Math.max(...Object.values(dailyData));

  return (
    <ThemeProvider theme={theme}>
      <StyledCard title={<Title level={4} style={{ margin: 0 }}>Daily Breakdown</Title>}>
        {days.map((day) => {
          const value = dailyData[day];
          const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
          
          return (
            <DailyBreakdownItem key={day}>
              <DailyBreakdownHeader>
                <DailyBreakdownDay>{day}</DailyBreakdownDay>
                <DailyBreakdownAmount>
                  ${addCommasInPrices(value)}
                </DailyBreakdownAmount>
              </DailyBreakdownHeader>
              <StyledProgress
                percent={percentage}
                showInfo={false}
                strokeColor={{ '0%': '#3b82f6', '100%': '#2563eb' }}
              />
            </DailyBreakdownItem>
          );
        })}
      </StyledCard>
    </ThemeProvider>
  );
};

export default DailyBreakdownSection;
